import { flatten } from 'fp-ts/Array';
import { flow, pipe } from 'fp-ts/function';
import { groupBy, head, NonEmptyArray, map as nonEmptyArrayMap } from 'fp-ts/NonEmptyArray';
import { collect, filter, map } from 'fp-ts/Record';

import { ApplicationState } from '../../../types';
import { WithCurrencyCode } from '../../../types/selectors/selectorPropTypes';
import { getPricingHash } from '../../pricing/getPricingHash';
import { hasPricingCurrencyCodeByHash } from '../../pricing/selectors';
import { OccupancyData } from '../../resourceOrder/occupancyTypes';
import { getReservations } from './selectors';
import { Reservation } from './types';

const getReservationPricingHash = ({
    voucherCode,
    productIds,
    roomId: categoryId,
    hotelId: enterpriseId,
    endDate,
    startDate,
    occupancyData = [],
}: Reservation) =>
    getPricingHash({
        startDate,
        endDate,
        enterpriseId,
        categoryId,
        voucherCode,
        productIds,
        occupancyData,
    });

interface Occupancy {
    occupancyData: OccupancyData[];
}

interface ReservationWithOccupancies extends Omit<Reservation, 'adultCount' | 'childCount' | 'occupancyData'> {
    occupancies: Occupancy[];
}

const reservationOccupancyCollector = (
    key: string,
    { adultCount, childCount, occupancyData, ...other }: Reservation
): ReservationWithOccupancies => ({
    ...other,
    occupancies: [{ occupancyData: occupancyData ?? [] }],
});

const getPricingGroupStringWithoutOccupancy = ({ occupancies, ...reservationData }: ReservationWithOccupancies) =>
    getReservationPricingHash({
        ...reservationData,
        adultCount: 0,
        childCount: 0,
        occupancyData: [],
        startDate: reservationData.startTimeUtc ?? reservationData.startDate,
        endDate: reservationData.endTimeUtc ?? reservationData.endDate,
    });

const collectRecordAsArray = <T>(key: string, item: T) => item;

const mergeOccupancies = (
    groupedReservations: NonEmptyArray<ReservationWithOccupancies>
): ReservationWithOccupancies => {
    const allOccupanciesInGroup = pipe(
        groupedReservations,
        nonEmptyArrayMap(({ occupancies }) => occupancies),
        flatten
    );
    return pipe(groupedReservations, head, (groupedReservation) => ({
        ...groupedReservation,
        occupancies: allOccupanciesInGroup,
    }));
};

const getReservationsWithoutCurrencyCode = (state: ApplicationState, { currencyCode }: WithCurrencyCode) =>
    pipe(
        getReservations(state),
        filter((reservation) => {
            const pricingHash = getReservationPricingHash(reservation);

            return !hasPricingCurrencyCodeByHash(state, {
                rateId: reservation.rateId,
                currencyCode,
                pricingHash,
            });
        })
    );

export const getReservationsWithoutPricingCurrencyCode = flow(
    getReservationsWithoutCurrencyCode,
    collect(reservationOccupancyCollector),
    groupBy(getPricingGroupStringWithoutOccupancy),
    map(mergeOccupancies),
    collect(collectRecordAsArray)
);
